import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// admin#175 — shows a freshly issued client secret exactly once.
//
// React-controlled rather than a data-toggle Bootstrap modal so it cannot be
// dismissed by a backdrop click or Escape: the only way out is ticking
// "I've saved it". The secret lives only in the parent's state, which the
// parent clears in onClose.
function OneTimeSecretModal({ clientId, clientSecret, title = 'Client credentials', onClose }) {
    const [saved, setSaved] = useState(false);
    const savedCheckboxRef = useRef(null);

    // Take focus from whatever submitted the form behind the modal, so the
    // keyboard acts on the modal, not the page under it.
    useEffect(() => {
        savedCheckboxRef.current?.focus();
    }, []);

    // Refresh, closing the tab or leaving the site would lose the secret for
    // good; the only fix after that is another rotation. Ask first.
    useEffect(() => {
        if (saved) return undefined;
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [saved]);

    const copy = async (label, value) => {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(`${label} copied.`);
        } catch {
            toast.error('Copy failed. Select the text and copy it manually.');
        }
    };

    return (
        <>
            <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="one-time-secret-title">
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="one-time-secret-title">{title}</h5>
                        </div>
                        <div className="modal-body txt_lft">
                            <div className="alert alert-warning" role="alert">
                                <strong>This secret won&apos;t be shown again.</strong> Send it to the partner by
                                one-time link, never by email or chat.
                            </div>

                            <label className="fwt_600" htmlFor="one-time-client-id">Client ID</label>
                            <div className="input-group mb-3">
                                <input id="one-time-client-id" className="form-control" value={clientId} readOnly />
                                <div className="input-group-append">
                                    <button type="button" className="btn btn-outline-secondary" onClick={() => copy('Client ID', clientId)}>
                                        Copy
                                    </button>
                                </div>
                            </div>

                            <label className="fwt_600" htmlFor="one-time-client-secret">Client secret</label>
                            <div className="input-group mb-3">
                                <input
                                    id="one-time-client-secret"
                                    className="form-control"
                                    value={clientSecret}
                                    readOnly
                                    autoComplete="off"
                                    spellCheck={false}
                                />
                                <div className="input-group-append">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => copy('Client secret', clientSecret)}
                                    >
                                        Copy
                                    </button>
                                </div>
                            </div>

                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="one-time-secret-saved"
                                    ref={savedCheckboxRef}
                                    checked={saved}
                                    onChange={(e) => setSaved(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="one-time-secret-saved">
                                    I&apos;ve saved it
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-blue" disabled={!saved} onClick={onClose}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show" />
        </>
    );
}

export default OneTimeSecretModal;
